import {
  Button,
  Image,
  Group,
  LoadingOverlay,
  NumberInput,
  Select,
  Stack,
  TextInput,
  Text,
  rem,
  Divider,
} from "@mantine/core"
import { DefaultModal } from "../../../../-components/default-modal"
import { useForm } from "@mantine/form"
import { useNavigate } from "@tanstack/react-router"
import { PriceInput } from "./PriceInput"
import {
  useGetLocationsSuspense,
  useGetResourceTypeCategoriesSuspense,
  useCreateResource,
} from "@/orval/gen"
import { SelectEntity } from "../../../../-components/entity-select"
import { ResourceTypeCategory } from "@/orval/gen/model"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import { ResourceTypeCategorySelect } from "./ResourceTypeCategorySelect"
import {
  showErrorNotification,
  showSuccessNotification,
} from "@/utils/form-submission-notifications"
import { DefaultDropzone } from "@/-components/DefaultDropzone"
import { FileWithPath, Dropzone, MIME_TYPES } from "@mantine/dropzone"
import { IconUpload, IconX, IconPhoto } from "@tabler/icons-react"
import { useDefaultAttachmentsApiForControllerRoute } from "@/attachment-api"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { CreateResourceObjectsModal } from "./CreateResourceObjectsModal"
import { DescriptionHoverCard } from "../../-components/DescriptionHoverCard"

interface Props {
  opened: boolean
  onClose(): void
  defaultCategory?: ResourceTypeCategory
}

export function CreateResourceModal({
  opened,
  onClose,
  defaultCategory,
}: Props) {
  const { t } = useTranslation("app_server")
  const navigate = useNavigate()
  const { data: categories } = useGetResourceTypeCategoriesSuspense()
  const { data: locations } = useGetLocationsSuspense({
    locationTypes: ["Store"],
  })

  const [lastOwnerGroupId, setLastOwnerGroupId] = useUserLocalStorage<
    number | null
  >({
    key: "create-resource-last-owner-group",
    defaultValue: null,
  })

  const [filesToUpload, setFilesToUpload] = React.useState<
    FileWithPath[] | undefined
  >(undefined)

  const [rentalFeeInput, setRentalFeeInput] = React.useState("")
  const [cleaningCostInput, setCleaningCostInput] = React.useState("")

  const [createdResourceId, setCreatedResourceId] = React.useState<
    number | null
  >(null)
  const [showResourceObjectsModal, setShowResourceObjectsModal] =
    React.useState(false)

  const form = useForm({
    initialValues: {
      name: "",
      count: 1,
      locationId: null as number | null,
      ownerGroupId: lastOwnerGroupId,
      resourceTypeId: undefined as string | undefined,
      category: defaultCategory ?? categories[0],
      rentalFee: undefined as number | undefined,
      cleaningCost: undefined as number | undefined,
    },
  })

  React.useEffect(() => {
    if (opened) {
      form.setFieldValue("category", defaultCategory ?? categories[0])
      form.setFieldValue("ownerGroupId", lastOwnerGroupId)
      setFilesToUpload(undefined)
      setShowResourceObjectsModal(false)
      setCreatedResourceId(null)
    } else {
      form.reset()
      setFilesToUpload(undefined)
    }
  }, [opened, defaultCategory, categories, lastOwnerGroupId])

  React.useEffect(() => {
    form.setFieldValue("resourceTypeId", "")
  }, [form.values.category])

  const handleResourceObjectsModalClose = () => {
    setShowResourceObjectsModal(false)
    if (createdResourceId) {
      onClose()
      navigate({
        to: "/app/resources/$id",
        params: { id: createdResourceId.toString() },
      })
    }
  }

  const queryClient = useQueryClient()

  const { mutateAsync: createResourceAsync, isPending } = useCreateResource()
  const attachmentApi = useDefaultAttachmentsApiForControllerRoute("resources")

  const handleSubmit = async (
    values: typeof form.values & { resourceTypeId?: string }
  ) => {
    if (!values.resourceTypeId)
      return showErrorNotification(t("SelectResourceTypeError"))

    const rawTypeValue = values.resourceTypeId ?? ""
    let resourceTypeIdNum = NaN
    if (rawTypeValue.startsWith("ResourceTypeId-")) {
      const parts = rawTypeValue.split("-")
      resourceTypeIdNum = Number(parts[parts.length - 1])
    } else if (rawTypeValue.startsWith("ResourceTypeCategoryId-")) {
      return showErrorNotification(t("SelectResourceNotCategoryError"))
    } else {
      resourceTypeIdNum = Number(rawTypeValue)
    }

    if (Number.isNaN(resourceTypeIdNum) || resourceTypeIdNum <= 0)
      return showErrorNotification(t("SelectValidResourceTypeError"))

    const categoryVal = values.category as unknown as string
    if (!categoryVal || !categories.includes(categoryVal as any))
      return showErrorNotification(t("InvalidCategory"))

    const locationIdNum = values.locationId ? Number(values.locationId) : 0
    const ownerGroupIdNum = Number(values.ownerGroupId)

    const payload = {
      resourceTypeId: resourceTypeIdNum,
      name: values.name,
      count: Number(values.count) || 1,
      locationId: locationIdNum,
      ownerGroupId: ownerGroupIdNum,
      resourceFields: [],
      rentalFee: values.rentalFee ?? null,
      cleaningCost: values.cleaningCost ?? null,
      category: categoryVal as unknown as ResourceTypeCategory,
    }

    try {
      const created = await createResourceAsync({ data: payload })

      if (filesToUpload && filesToUpload.length > 0) {
        const file = filesToUpload[0]
        const partialMetadata = {
          resourceId: created.resourceId,
          attachment: {
            attachmentName: file.name,
            ownerGroupId: payload.ownerGroupId,
          },
          sort: 0,
          note: "",
        }

        await attachmentApi.addAttachmentAndMetadata(
          file as File,
          partialMetadata as any,
          created.resourceId
        )
      }

      queryClient.invalidateQueries({ queryKey: ["getPaginatedResources"] })
      setLastOwnerGroupId(payload.ownerGroupId)
      showSuccessNotification(t("Created"))

      // If count > 0, show resource objects modal instead of navigating
      if (Number(values.count) > 0) {
        setCreatedResourceId(created.resourceId)
        setShowResourceObjectsModal(true)
      } else {
        onClose()
        navigate({
          to: "/app/resources/$id",
          params: { id: created.resourceId.toString() },
        })
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        showErrorNotification(e)
      } else {
        showErrorNotification(String(e))
      }
    }
  }

  return (
    <>
      <DefaultModal opened={opened} onClose={onClose} title={t("New")}>
        <LoadingOverlay visible={isPending} />
        <form onSubmit={form.onSubmit((v) => handleSubmit(v))}>
          <DefaultDropzone
            value={filesToUpload as any}
            onFileChange={(f) => setFilesToUpload(f as FileWithPath[])}
            onDrop={(f) => setFilesToUpload(f as FileWithPath[])}
            multiple={false}
            accept={[MIME_TYPES.png, MIME_TYPES.jpeg]}
            maxFiles={1}
          >
            <Group
              justify="center"
              gap="xl"
              mih={140}
              style={{ pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <IconUpload
                  style={{
                    width: rem(40),
                    height: rem(40),
                    color: "var(--mantine-color-blue-6)",
                  }}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  style={{
                    width: rem(40),
                    height: rem(40),
                    color: "var(--mantine-color-red-6)",
                  }}
                  stroke={1.5}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                {filesToUpload && filesToUpload.length > 0 ? (
                  <Image
                    src={URL.createObjectURL(filesToUpload[0])}
                    fit="contain"
                  />
                ) : (
                  <IconPhoto
                    style={{
                      width: rem(40),
                      height: rem(40),
                      color: "var(--mantine-color-dimmed)",
                    }}
                    stroke={1.5}
                  />
                )}
              </Dropzone.Idle>
            </Group>
          </DefaultDropzone>
          <Divider />
          <Stack>
            <ResourceTypeCategorySelect
              label={t("Category")}
              value={form.values.category}
              onChange={(v) => form.setFieldValue("category", v)}
            />
            <ResourceTypeTreeSelect
              key={`${form.values.category}-${form.values.resourceTypeId}`}
              label={t("Type")}
              category={form.values.category}
              placeholder={t("SelectResourceType")}
              initialCheckedState={
                form.values.resourceTypeId ? [form.values.resourceTypeId] : []
              }
              onCheckedStateChange={(values) => {
                if (values.length > 1) {
                  const newValue = values.find(
                    (v) => v !== form.values.resourceTypeId
                  )
                  form.setFieldValue(
                    "resourceTypeId",
                    newValue ?? values[0] ?? ""
                  )
                } else {
                  form.setFieldValue("resourceTypeId", values[0] ?? "")
                }
              }}
            />
            <TextInput
              required
              label={t("Name")}
              {...form.getInputProps("name")}
            />
            <NumberInput
              label={
                <>
                  <Text size="sm" fw={500}>
                    {t("Count")}
                  </Text>
                  <DescriptionHoverCard
                    anchorText={t("WhatDoesThatMean")}
                    description={
                      <Text component="span" size="xs">
                        {t("CreateResource_CountDescription")}
                      </Text>
                    }
                  />
                </>
              }
              min={0}
              {...form.getInputProps("count")}
            />
            <Select
              label={t("StoringLocation")}
              data={locations.map((l) => ({
                value: String(l.locationId),
                label: l.name ?? "",
              }))}
              placeholder={t("SelectLocation")}
              value={
                form.values.locationId ? String(form.values.locationId) : ""
              }
              onChange={(v) =>
                form.setFieldValue("locationId", v ? Number(v) : null)
              }
              clearable
            />
            <SelectEntity
              label={t("Owner")}
              url="/api/OwnerGroup/GetSimpleOwnerGroupsByPermissionArea"
              toRequest={() => ({
                permissionArea: "Resources",
                onlyWithWritePermission: true,
              })}
              getLabel={(v) => v.name}
              getId={(v) => v.ownerGroupId}
              value={form.values.ownerGroupId}
              onChange={(v) => form.setFieldValue("ownerGroupId", v ?? null)}
              required
            />
            {form.values.category === "Costume" && (
              <>
                <PriceInput
                  label={t("RentalFee")}
                  value={rentalFeeInput}
                  onChange={(cleanedInput, numValue) => {
                    setRentalFeeInput(cleanedInput)
                    form.setFieldValue("rentalFee", numValue)
                  }}
                />
                <PriceInput
                  label={t("CleaningCost")}
                  value={cleaningCostInput}
                  onChange={(cleanedInput, numValue) => {
                    setCleaningCostInput(cleanedInput)
                    form.setFieldValue("cleaningCost", numValue)
                  }}
                />
              </>
            )}
            <Group>
              <Button variant="default" onClick={onClose}>
                {t("Cancel")}
              </Button>
              <Button type="submit">{t("Create")}</Button>
            </Group>
          </Stack>
        </form>
      </DefaultModal>
      <CreateResourceObjectsModal
        opened={showResourceObjectsModal}
        onClose={handleResourceObjectsModalClose}
        defaultResourceId={createdResourceId ?? undefined}
        category={form.values.category}
      />
    </>
  )
}
